/*
 * Copyright (c) 2023 - 2026 Bluemate B.V.
 */
import './style/bolOauth.scss';

import {AwesomeIcon, ErrorType, Loader, voidFunction} from '@dvrd/dvr-controls';

import classNames from 'classnames';

import React, {MouseEventHandler} from 'react';

import {useTranslation} from 'react-i18next';

import {APIValidationStatus} from '../../../util/interfaces';

import {BluemateButton} from '../../controls';

import BluemateDialog from '../../controls/dialog/bluemateDialog';

import {IconName} from '@fortawesome/fontawesome-svg-core';

interface Props {
    onClose: VoidFunction;
    onRetry: MouseEventHandler;
    status: APIValidationStatus;
    error: ErrorType;
}

export default function BolOauth(props: Props) {
    const {onClose, onRetry, status, error} = props;
    const {t} = useTranslation();

    function renderStatus() {
        let statusLabel: string;
        if (status === APIValidationStatus.VALIDATING)
            statusLabel = t('oauth.bol.statusValidating', 'Koppeling testen...');
        else if (status === APIValidationStatus.ERROR) statusLabel = t('oauth.bol.statusError', 'Autorisatie mislukt');
        else statusLabel = t('oauth.bol.statusSuccess', 'Koppeling voltooid');
        return <label className='status-label'>{statusLabel}</label>;
    }

    function renderStatusIndicator() {
        let icon: IconName = 'circle-check';
        let iconClass = 'validated';
        if (status === APIValidationStatus.VALIDATING)
            return (
                <>
                    <Loader position='relative' active disableBackground />
                    <label>{t('oauth.bol.testing', 'Eén moment, we testen de verbinding.')}</label>
                </>
            );
        else if (status === APIValidationStatus.ERROR) {
            icon = 'circle-xmark';
            iconClass = 'error';
        }
        return <AwesomeIcon name={icon} className={classNames('status-indicator', iconClass)} />;
    }

    function renderError() {
        if (!error) return null;
        return <label className='error-label'>{error}</label>;
    }

    function renderButton() {
        let label = t('oauth.bol.continue', 'Doorgaan');
        let onClick: MouseEventHandler = onClose;
        const disabled = status === APIValidationStatus.VALIDATING;
        const secondary = status === APIValidationStatus.ERROR;
        if (status === APIValidationStatus.ERROR) {
            label = t('oauth.bol.retry', 'Opnieuw proberen');
            onClick = onRetry;
        }
        return <BluemateButton onClick={onClick} label={label} disabled={disabled} secondary={secondary} />;
    }

    return (
        <BluemateDialog
            active
            // Non-dismissable dialog (like the "Abonnement is gepauzeerd" paywall): no close (X), no
            // cancel, and a backdrop click does nothing (onClose is a no-op). The user leaves via the
            // in-body button (retry / continue) or the navigation menu.
            onClose={voidFunction}
            title={t('oauth.bol.title', 'Koppeling met Bol')}
            hideClose
            hideCancel
            // Only dim the page content — like the paywall — so the header and navigation menu stay
            // interactive: a failed authorization never blocks the whole app even though the dialog
            // itself can't be dismissed (reloading re-runs the callback, so it would otherwise trap the
            // user).
            backdropVariant='content'
            width='40rem'
        >
            <div className='bol-oauth-body'>
                {renderStatus()}
                {renderStatusIndicator()}
                {renderError()}
                {renderButton()}
            </div>
        </BluemateDialog>
    );
}
